import { Slider as MUISlider, styled } from '@mui/material';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { Input } from '@shadcn/components/input';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface ContentFilterProps {
  numLevels: number;
  onRangeChange?: (min: number, max: number) => void;
  onSearchChange?: (query: string) => void;
}
const ContentFilter = ({
  numLevels,
  onRangeChange,
  onSearchChange,
}: ContentFilterProps) => {
  const [range, setRange] = useState<[number, number]>([1, numLevels]);
  const [search, setSearch] = useState('');

  // Debounce the range change notification to parent
  const debouncedOnRangeChange = useMemo(
    () =>
      debounce((min: number, max: number) => {
        onRangeChange?.(min, max);
      }, 200),
    [onRangeChange],
  );

  const debouncedOnSearchChange = useMemo(
    () =>
      debounce((query: string) => {
        onSearchChange?.(query);
      }, 150),
    [onSearchChange],
  );

  // Handle range changes locally first
  const handleRangeChange = useCallback(
    (value: [number, number]) => {
      setRange(value);
      debouncedOnRangeChange(value[0], value[1]);
    },
    [debouncedOnRangeChange],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
      debouncedOnSearchChange(e.target.value);
    },
    [debouncedOnSearchChange],
  );

  // Cleanup
  useEffect(() => {
    return () => {
      debouncedOnRangeChange.cancel();
      debouncedOnSearchChange.cancel();
    };
  }, [debouncedOnRangeChange, debouncedOnSearchChange]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="text-xs font-medium text-muted-foreground min-w-[50px]">
          Levels
        </div>
        <div className="flex-1 px-2">
          <StyledSlider
            value={range}
            min={1}
            max={numLevels}
            step={1}
            onChange={(_, value) =>
              handleRangeChange(value as [number, number])
            }
            valueLabelDisplay="off"
            disableSwap
          />
        </div>
        <div className="text-xs tabular-nums text-muted-foreground min-w-[40px] text-right">
          {range[0]}-{range[1]}
        </div>
      </div>
      <div className="relative">
        <MagnifyingGlassIcon
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          size={14}
        />
        <Input
          placeholder="Search content..."
          value={search}
          onChange={handleSearchChange}
          className="h-8 pl-8 text-xs"
        />
      </div>
    </div>
  );
};

export default ContentFilter;

const StyledSlider = styled(MUISlider)(() => ({
  height: 4,
  padding: '13px 0',
  '& .MuiSlider-thumb': {
    height: 16,
    width: 16,
    backgroundColor: 'hsl(var(--background))',
    border: '1px solid hsl(var(--primary) / 0.5)',
    '&:hover': {
      borderColor: 'hsl(var(--primary))',
      backgroundColor: 'hsl(var(--accent))',
    },
    '&:focus-visible': {
      outline: 'none',
      boxShadow: '0 0 0 2px hsl(var(--background)), 0 0 0 4px hsl(var(--ring))',
    },
  },
  '& .MuiSlider-track': {
    height: 4,
    backgroundColor: 'hsl(var(--primary))',
  },
  '& .MuiSlider-rail': {
    height: 4,
    backgroundColor: 'hsl(var(--secondary))',
    opacity: 1,
  },
}));
